// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'axios';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

export interface BlogPost {
    id: string;
    title: string;
    slug: string;
    description: string;
    content: string;
    author: string;
    readTime: string;
    tags: string[];
    status: 'draft' | 'published' | 'rejected';
    imagePrompt?: string;
    imageUrl?: string;
    feedback?: string;
    publishedAt: string;
    refreshBy: string;
    createdAt: string;
    updatedAt: string;
}

interface BlogState {
    posts: BlogPost[];
    currentPost: BlogPost | null;
    loading: boolean;
    error: any;
}

const initialState: BlogState = {
    posts: [],
    currentPost: null,
    loading: false,
    error: null
};

const slice = createSlice({
    name: 'blog',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },

        // SET LOADING
        setLoading(state) {
            state.loading = true;
        },

        // GET BLOG POSTS
        getBlogPostsSuccess(state, action) {
            state.posts = action.payload;
            state.loading = false;
        },

        // GET BLOG POST
        getBlogPostSuccess(state, action) {
            state.currentPost = action.payload;
            state.loading = false;
        },

        // UPDATE BLOG POST
        updateBlogPostSuccess(state, action) {
            state.currentPost = action.payload;
            state.loading = false;
        },

        // APPROVE BLOG POST
        approveBlogPostSuccess(state, action) {
            state.currentPost = action.payload;
            const idx = state.posts.findIndex((p) => p.id === action.payload.id);
            if (idx !== -1) state.posts[idx] = action.payload;
            state.loading = false;
        },

        // REJECT BLOG POST
        rejectBlogPostSuccess(state, action) {
            state.currentPost = action.payload;
            const idx = state.posts.findIndex((p) => p.id === action.payload.id);
            if (idx !== -1) state.posts[idx] = action.payload;
            state.loading = false;
        },

        // DELETE BLOG POST
        deleteBlogPostSuccess(state, action) {
            state.posts = state.posts.filter((post) => post.id !== action.payload);
            state.loading = false;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getBlogPosts(status?: string, page?: number) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading());
            const response = await axios({
                url: `${process.env.REACT_APP_API_URL}/admin/blog`,
                method: 'GET',
                params: { status, page, order: 'DESC', take: 50 }
            });
            dispatch(slice.actions.getBlogPostsSuccess(response.data.data || response.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getBlogPost(id: string) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading());
            const response = await axios({
                url: `${process.env.REACT_APP_API_URL}/admin/blog/${id}`,
                method: 'GET'
            });
            dispatch(slice.actions.getBlogPostSuccess(response.data.data || response.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateBlogPost(id: string, data: Partial<BlogPost>) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading());
            const response = await axios({
                url: `${process.env.REACT_APP_API_URL}/admin/blog/${id}`,
                method: 'PUT',
                data
            });
            dispatch(slice.actions.updateBlogPostSuccess(response.data.data || response.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function approveBlogPost(id: string) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading());
            const response = await axios({
                url: `${process.env.REACT_APP_API_URL}/admin/blog/${id}/approve`,
                method: 'POST'
            });
            dispatch(slice.actions.approveBlogPostSuccess(response.data.data || response.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function rejectBlogPost(id: string, feedback: string) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading());
            const response = await axios({
                url: `${process.env.REACT_APP_API_URL}/admin/blog/${id}/reject`,
                method: 'POST',
                data: { feedback }
            });
            dispatch(slice.actions.rejectBlogPostSuccess(response.data.data || response.data));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function deleteBlogPost(id: string) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading());
            await axios({
                url: `${process.env.REACT_APP_API_URL}/admin/blog/${id}`,
                method: 'DELETE'
            });
            dispatch(slice.actions.deleteBlogPostSuccess(id));
        } catch (error) {
            console.error(error);
            dispatch(slice.actions.hasError(error));
        }
    };
}
